import React, { useState } from 'react';
import './NoteJdmaPill.css';

// ═══ Notes de l'atelier (JDMA + Google) — pastille d'en-tête + panneau ═════
//
// Gabarit compact 2026-09-07 (demande utilisateur) : la pastille porte LES
// DEUX notes — « ★ 3,4 JDMA · 4,6 Google » — et perd sa barre de répartition
// et son étiquette « 30 j », qui vivent désormais dans le panneau au survol.
// MÊME FENÊTRE pour les deux notes : 30 jours glissants, même seuil de
// fiabilité (10 avis) — décision utilisateur 2026-09-07, qui revient sur la
// note globale de la fiche affichée un temps. La note globale reste visible
// dans le panneau, en info secondaire (c'est celle que le public voit sur
// Google).
//
// Parti pris de conception, tiré des données réelles (26/08/2026) :
//
// • La note est presque IMMOBILE : un avis 5★ la déplace de +0,02. Affichée
//   seule, elle devient du papier peint en une semaine. D'où la barre de
//   répartition, qui montre ce que la moyenne cache.
// • La moyenne ne décrit personne : BLUE, c'est 31 avis à 5★ ET 17 à 0-2★.
//   « 3,5 » est le milieu d'un trou.
// • L'écart 30 j / 30 j précédents est du BRUIT (3,44 à 4,13 sur six mois, sans
//   tendance) : pas de flèche de tendance, une courbe 6 mois à la place.
// • Le chiffre n'est JAMAIS coloré (arbitrage utilisateur) : un « 3,5 » écarlate
//   en permanence devant 37 personnes use plus qu'il ne motive. La couleur vit
//   dans la barre de répartition, où elle décrit des faits et non un jugement.
//
// ⚠️ EMPLACEMENT : l'en-tête de page, entre le titre et le sélecteur
// Atelier/Réception (demande utilisateur 2026-08-26). Elle vivait d'abord dans
// `.atelier-tabs` — mesuré, elle y coûtait 173 px dans une barre qui déborde
// déjà : sous 1660 px elle poussait la loupe hors du cadre, et sous 1280 px elle
// rendait deux onglets d'atelier inatteignables. L'en-tête, lui, a 783 px libres
// à 1920 px et encore 143 px à 1280 px.
// Contrepartie assumée : l'en-tête n'est pas rendu en PLEIN ÉCRAN
// (`requestFullscreen()` porte sur la grille seule) — la pastille y disparaît.
// Acceptable pour un indicateur : c'est la raison inverse qui avait fait
// DESCENDRE la loupe dans la barre, elle, étant opérationnelle.

const fmtNote = (n) => (n == null ? '—' : n.toFixed(1).replace('.', ','));

// Seuil Google : porté par le serveur, mais recalculé ici en repli — une
// réponse mise en cache par un onglet ouvert pendant un déploiement peut ne
// pas encore porter le drapeau (vécu le 2026-09-07 : « moyenne non affichée »
// avec 46 avis). Même seuil que JDMA (10).
const seuilGoogleOk = (g) => !!g && (g.seuil_atteint ?? g.avis_30j >= 10);

// Courbe des six derniers mois. Volontairement sans axes ni libellés : elle ne
// sert pas à lire une valeur (le panneau donne les chiffres) mais à voir que la
// note OSCILLE — ce qui désamorce la lecture « on a chuté ce mois-ci ».
function QsCourbe({ points }) {
  if (!points || points.length < 2) return null;
  const notes = points.map(p => p.note);
  const min = Math.min(...notes), max = Math.max(...notes);
  const etendue = max - min || 1;
  const W = 260, H = 34, m = 5;
  const xy = points.map((p, i) => [
    m + (i * (W - 2 * m)) / (points.length - 1),
    H - m - ((p.note - min) / etendue) * (H - 2 * m),
  ]);
  const [lx, ly] = xy[xy.length - 1];
  return (
    <svg
      className="qs-courbe" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
      aria-label={`Note mensuelle des ${points.length} derniers mois : ${points.map(p => fmtNote(p.note)).join(', ')}`}
    >
      <polyline points={xy.map(([x, y]) => `${x},${y}`).join(' ')} fill="none" strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="3.5" />
    </svg>
  );
}

function QsPanneau({ q, peutOuvrirJdma, peutOuvrirGmb }) {
  const r = q.repartition;
  const maxBarre = Math.max(r.n5, r.n4, r.n3, r.n02, 1);
  const lignes = [
    { cle: 'n5',  lbl: '5 ★',   v: r.n5,  cls: 'qs-s5' },
    { cle: 'n4',  lbl: '4 ★',   v: r.n4,  cls: 'qs-s4' },
    { cle: 'n3',  lbl: '3 ★',   v: r.n3,  cls: 'qs-s3' },
    { cle: 'n02', lbl: '0-2 ★', v: r.n02, cls: 'qs-s0' },
  ];
  // Un avis reçu sans note n'est pas une note : il compte dans les réponses,
  // jamais dans la moyenne (même règle que le tableau de bord JDMA).
  const sansNote = q.recus - q.notes;
  return (
    <div className="qs-pop" role="dialog" aria-label="Qualité de service">
      <div className="qs-pop-head">
        <b>Qualité de service</b>
        <span>Entretien · APV</span>
      </div>

      {q.seuil_atteint ? (
        <>
          <div className="qs-pop-big">{fmtNote(q.moyenne)}<small> / 5</small></div>
          <p className="qs-pop-sub">
            {q.notes} avis notés sur 30 jours
            {sansNote > 0 && <> · {sansNote} sans note</>}
          </p>
          <div className="qs-rows">
            {lignes.map(l => (
              <div className="qs-row" key={l.cle}>
                <span className="qs-lbl">{l.lbl}</span>
                <span className="qs-trk"><i className={l.cls} style={{ width: `${(l.v / maxBarre) * 100}%` }} /></span>
                <span className="qs-cnt">{l.v}</span>
              </div>
            ))}
          </div>
          {q.nudge && (
            <p className="qs-nudge">
              <b>{q.nudge.avis} avis à 5★</b> {q.nudge.avis > 1 ? 'suffisent' : 'suffit'} à passer la note à <b>{fmtNote(q.nudge.cible)}</b>.
              {r.n02 > 0 && (
                <><br /><span className="qs-nudge-sub">{r.n02} client{r.n02 > 1 ? 's ont' : ' a'} mis 2 ou moins ce mois-ci.</span></>
              )}
            </p>
          )}
          <QsCourbe points={q.evolution} />
        </>
      ) : (
        <p className="qs-pop-vide">
          {q.notes === 0
            ? 'Aucun avis noté sur les 30 derniers jours.'
            : `${q.notes} avis noté${q.notes > 1 ? 's' : ''} seulement sur 30 jours.`}
          <br />
          <span className="qs-nudge-sub">
            En dessous de {q.seuil}, une seule mauvaise note ferait chuter la moyenne d'un demi-point :
            elle n'est pas affichée.
          </span>
        </p>
      )}

      {q.google && (
        <div className="qs-google">
          <div className="qs-google-head">
            <b>Note Google</b>
            <span>{q.google.fiche || 'fiche Google Business'}</span>
          </div>
          {seuilGoogleOk(q.google) ? (
            <p className="qs-google-note">
              <b>{fmtNote(q.google.note_30j)}</b><small> / 5</small> · {q.google.avis_30j} avis sur 30 jours
            </p>
          ) : (
            <p className="qs-google-note qs-google-note--faible">
              {q.google.avis_30j === 0
                ? 'Aucun avis sur les 30 derniers jours.'
                : `${q.google.avis_30j} avis seulement sur 30 jours — moyenne non affichée.`}
            </p>
          )}
          <p className="qs-google-30j">
            Fiche Google : {fmtNote(q.google.note)}/5 · {q.google.avis.toLocaleString('fr-FR')} avis au total
          </p>
        </div>
      )}

      {(peutOuvrirJdma || (q.google && peutOuvrirGmb)) && (
        <div className="qs-pop-liens">
          {peutOuvrirJdma && (
            <a className="qs-pop-lien" href="/?page=jdma">Détail JDMA →</a>
          )}
          {q.google && peutOuvrirGmb && (
            <a className="qs-pop-lien" href="/?page=gmb-reviews">Avis Google →</a>
          )}
        </div>
      )}
    </div>
  );
}

// La pastille elle-même. Elle possède SON état d'ouverture : montée dans
// l'en-tête de page (Dashboard), elle ne doit pas faire re-rendre celui-ci —
// ni l'effet `setHeaderAction` du Planning — à chaque survol.
export default function NoteJdmaPill({ qualite, peutVoirJdma, peutVoirGmb }) {
  const [ouverte, setOuverte] = useState(false);
  if (!qualite) return null;
  const g = qualite.google;
  const titreJdma = qualite.seuil_atteint
    ? `JDMA 30 jours : ${fmtNote(qualite.moyenne)}/5 (${qualite.notes} avis notés)`
    : `JDMA : seulement ${qualite.notes} avis notés sur 30 jours — il en faut ${qualite.seuil} pour que la moyenne veuille dire quelque chose`;
  const gOk = seuilGoogleOk(g);
  const titreGoogle = g
    ? (gOk
        ? ` · Google 30 jours : ${fmtNote(g.note_30j)}/5 (${g.avis_30j} avis)`
        : ` · Google : seulement ${g.avis_30j} avis sur 30 jours — moyenne non affichée`)
    : '';
  return (
    <div
      className="qs-wrap"
      onMouseEnter={() => setOuverte(true)}
      onMouseLeave={() => setOuverte(false)}
    >
      <button
        type="button"
        className={`qs-pill${qualite.seuil_atteint ? '' : ' qs-pill--faible'}`}
        onClick={() => setOuverte(o => !o)}
        aria-expanded={ouverte}
        title={titreJdma + titreGoogle}
      >
        <span className="qs-star" aria-hidden="true">★</span>
        <span className="qs-val">{qualite.seuil_atteint ? fmtNote(qualite.moyenne) : '—'}</span>
        <span className="qs-src">JDMA</span>
        {g && (
          <>
            <span className="qs-sep" aria-hidden="true" />
            <span className="qs-val">{gOk ? fmtNote(g.note_30j) : '—'}</span>
            <span className="qs-src">Google</span>
          </>
        )}
      </button>
      {ouverte && <QsPanneau q={qualite} peutOuvrirJdma={peutVoirJdma} peutOuvrirGmb={peutVoirGmb} />}
    </div>
  );
}
